'use client';

import React, { useState } from 'react';
import { MapPin, User, ChevronRight, AlertTriangle, Link2, RefreshCw } from 'lucide-react';
import BranchTag from '@/components/ui/BranchTag';
import StatusBadge from '@/components/ui/StatusBadge';
import Avatar from '@/components/ui/Avatar';
import { useTeam } from '@/context/TeamContext';
import { useScheduleItems, ScheduleItem } from '@/hooks/useScheduleItems';

type Branch = 'live_events' | 'studio' | 'outreach' | 'internal';

const BRANCH_SECTION_CONFIG: Record<Branch, { label: string; borderClass: string; headerBg: string; headerText: string }> = {
  live_events: {
    label: 'Production · Live Events',
    borderClass: 'border-l-indigo-400',
    headerBg: 'bg-indigo-50',
    headerText: 'text-indigo-700',
  },
  studio: {
    label: 'Education · Studio',
    borderClass: 'border-l-teal-400',
    headerBg: 'bg-teal-50',
    headerText: 'text-teal-700',
  },
  outreach: {
    label: 'Development · Outreach',
    borderClass: 'border-l-amber-400',
    headerBg: 'bg-amber-50',
    headerText: 'text-amber-700',
  },
  internal: {
    label: 'Internal',
    borderClass: 'border-l-slate-300',
    headerBg: 'bg-slate-50',
    headerText: 'text-slate-600',
  },
};

function getTodayISO(): string {
  return new Date().toISOString().split('T')[0];
}

export default function TodayScheduleColumn() {
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const { activeTeam } = useTeam();
  const { items: allItems, loading, refetch } = useScheduleItems();

  const today = getTodayISO();

  // Filter to today's items only
  const todayItems = allItems.filter((item) => item.date === today);

  // Filter by team branch
  const isLeadership = activeTeam.id === 'leadership';
  const filteredItems = isLeadership
    ? todayItems
    : todayItems.filter((item) => activeTeam.branches.includes(item.branch));

  const branchOrder: Branch[] = ['live_events', 'studio', 'outreach', 'internal'];

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-stone-100 shadow-card px-5 py-10 flex items-center justify-center">
        <div className="w-5 h-5 border-2 border-indigo-200 border-t-indigo-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (isLeadership) {
    const grouped = branchOrder
      .map((branch) => ({
        branch,
        items: filteredItems.filter((i) => i.branch === branch),
      }))
      .filter((g) => g.items.length > 0);

    return (
      <div className="space-y-4">
        {grouped.length === 0 && (
          <EmptyToday onRefetch={refetch} />
        )}
        {grouped.map(({ branch, items }) => {
          const cfg = BRANCH_SECTION_CONFIG[branch as Branch] ?? BRANCH_SECTION_CONFIG['internal'];
          return (
            <div key={branch} className="bg-white rounded-xl border border-stone-100 shadow-card overflow-hidden">
              <div className={`px-5 py-3 border-b border-stone-100 flex items-center justify-between ${cfg.headerBg}`}>
                <h3 className={`text-[13px] font-semibold ${cfg.headerText}`}>{cfg.label}</h3>
                <span className="text-[11px] text-slate-400 font-medium">{items.length} items</span>
              </div>
              <div className="divide-y divide-stone-50">
                {items.map((item) => (
                  <ScheduleRow
                    key={item.id}
                    item={item}
                    isExpanded={expandedItem === item.id}
                    onToggle={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
                    borderClass={cfg.borderClass}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  const teamBranch = activeTeam.branches[0] as Branch;
  const cfg = BRANCH_SECTION_CONFIG[teamBranch] ?? BRANCH_SECTION_CONFIG['internal'];

  return (
    <div className="bg-white rounded-xl border border-stone-100 shadow-card overflow-hidden">
      <div className={`px-5 py-4 border-b border-stone-100 flex items-center justify-between ${cfg.headerBg}`}>
        <h2 className={`text-[15px] font-semibold ${cfg.headerText}`}>{cfg.label} · Today</h2>
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-slate-400 font-medium">{filteredItems.length} items</span>
          <button
            onClick={refetch}
            title="Refresh"
            className="p-1 rounded-md hover:bg-white/60 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <RefreshCw size={12} />
          </button>
        </div>
      </div>

      {filteredItems.length === 0 ? (
        <EmptyToday onRefetch={refetch} />
      ) : (
        <div className="divide-y divide-stone-50">
          {filteredItems.map((item) => (
            <ScheduleRow
              key={item.id}
              item={item}
              isExpanded={expandedItem === item.id}
              onToggle={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
              borderClass={cfg.borderClass}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyToday({ onRefetch }: { onRefetch: () => void }) {
  return (
    <div className="px-5 py-12 text-center">
      <p className="text-[14px] font-medium text-slate-500">No items scheduled for today</p>
      <p className="text-[12px] text-slate-400 mt-1">
        Nothing in this team&apos;s branch for today.
      </p>
      <button
        onClick={onRefetch}
        className="mt-3 text-[12px] text-indigo-500 hover:text-indigo-700 flex items-center gap-1 mx-auto"
      >
        <RefreshCw size={11} /> Refresh
      </button>
    </div>
  );
}

interface ScheduleRowProps {
  item: ScheduleItem;
  isExpanded: boolean;
  onToggle: () => void;
  borderClass: string;
}

function ScheduleRow({ item, isExpanded, onToggle, borderClass }: ScheduleRowProps) {
  const hasIssue = item.has_conflict || (item.missing_logistics?.length ?? 0) > 0;

  return (
    <div
      className={`
        group transition-colors duration-150 border-l-2 ${borderClass}
        ${hasIssue ? 'bg-amber-50/40' : 'hover:bg-stone-50/60'}
      `}
    >
      <button
        className="w-full text-left px-5 py-3.5"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <div className="flex items-start gap-3">
          <div className="min-w-[72px]">
            <p className="text-[12px] font-semibold text-slate-700 tabular-nums">{item.time_start}</p>
            {item.time_end && (
              <p className="text-[11px] text-slate-400 tabular-nums">{item.time_end}</p>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[13.5px] font-semibold text-slate-800 leading-snug">{item.title}</p>
              {hasIssue && <AlertTriangle size={12} className="text-amber-500 shrink-0" />}
            </div>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <BranchTag branch={item.branch as Branch} size="xs" />
              <StatusBadge status={item.status} size="xs" />
              {item.location && (
                <span className="flex items-center gap-1 text-[11px] text-slate-400">
                  <MapPin size={10} />
                  <span className="truncate max-w-[160px]">{item.location}</span>
                </span>
              )}
              {item.owner_name && (
                <span className="flex items-center gap-1 text-[11px] text-slate-400">
                  <User size={10} />
                  {item.owner_name}
                </span>
              )}
            </div>
          </div>
          <ChevronRight
            size={14}
            className={`text-slate-300 shrink-0 mt-0.5 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
          />
        </div>
      </button>

      {isExpanded && (
        <div className="px-5 pb-4 pt-1 border-t border-stone-50 bg-stone-50/40 animate-fade-in">
          <div className="flex items-start gap-4 flex-wrap">
            {item.owner_name && (
              <div className="flex items-center gap-2">
                <Avatar name={item.owner_name} size="sm" />
                <div>
                  <p className="text-[11px] text-slate-400">Owner</p>
                  <p className="text-[12px] font-medium text-slate-700">{item.owner_name}</p>
                </div>
              </div>
            )}
            {item.location && (
              <div>
                <p className="text-[11px] text-slate-400">Location</p>
                <p className="text-[12px] font-medium text-slate-700">{item.location}</p>
              </div>
            )}
            {(item.missing_logistics?.length ?? 0) > 0 && (
              <div>
                <p className="text-[11px] text-amber-600 font-semibold mb-1">Missing logistics</p>
                <ul className="space-y-0.5">
                  {item.missing_logistics.map((m, i) => (
                    <li key={i} className="text-[11px] text-amber-700 flex items-center gap-1">
                      <AlertTriangle size={10} />
                      {m}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {item.room_id && (
              <div>
                <p className="text-[11px] text-slate-400">Room</p>
                <p className="text-[12px] font-medium text-indigo-600 flex items-center gap-1">
                  <Link2 size={10} />
                  {item.room_id}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}